import { useCallback } from "react";

import { Typography } from "@mui/material";

import { AddShipmentTrackingLinkForm } from "../AddShipmentTrackingLinkForm";
import { ShipmentTrackingLinkActions } from "../ShipmentTrackingLinkActions";
import { ShipmentTrackingLinkDisplay } from "../ShipmentTrackingLinkDisplay";

interface ShipmentTrackingLinkPanelShipment {
  info: Pick<CondensedShipmentShipment, "id" | "type" | "booked_at">;
  shipment_status: ShipmentStatusValue;
  tracking_link_info: ShipmentTrackingLinkInfo;
}

interface Props {
  dividerComponent: JSX.Element;
  shipment: ShipmentTrackingLinkPanelShipment | undefined;
  onRefresh: VoidFunction | null;
  showWarningOnCopy: boolean;
  addButtonLabel: string;
  addPromptComponent: JSX.Element | null;
}

/*
The tracking link area of a shipment overview, shared by every role that may
manage the link. Renders nothing until the shipment is booked and can hold a
link, a read-only display once delivered, and the Add / Update / Delete
controls otherwise. Callers supply the role-specific copy and decide, before
rendering this, whether their role has write access to the shipment.
*/
export function ShipmentTrackingLinkPanel(props: Props) {
  const {
    dividerComponent,
    shipment,
    onRefresh,
    showWarningOnCopy,
    addButtonLabel,
    addPromptComponent,
  } = props;
  const refreshShipment = useCallback(() => onRefresh?.(), [onRefresh]);

  if (
    shipment?.info?.booked_at == null ||
    shipment.info.type === "MULTI_SHIPMENT_PROJECT" ||
    shipment.shipment_status === "BOOKING_CANCELLED"
  ) {
    return null;
  }

  const { tracking_link: trackingLink } = shipment.tracking_link_info;

  if (shipment.shipment_status === "DELIVERED") {
    return (
      <>
        {dividerComponent}
        {trackingLink !== null ? (
          <ShipmentTrackingLinkDisplay
            trackingLink={trackingLink}
            showWarningOnCopy={showWarningOnCopy}
          />
        ) : (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: "1rem", mb: "2rem" }}
          >
            No tracking link was added for this shipment.
          </Typography>
        )}
      </>
    );
  }

  if (trackingLink !== null) {
    return (
      <>
        {dividerComponent}
        <ShipmentTrackingLinkDisplay
          trackingLink={trackingLink}
          showWarningOnCopy={showWarningOnCopy}
          actions={
            <ShipmentTrackingLinkActions
              shipmentId={shipment.info.id}
              shipmentType={shipment.info.type}
              onRefresh={refreshShipment}
              trackingLinkId={trackingLink.id}
            />
          }
        />
      </>
    );
  }

  return (
    <>
      {dividerComponent}
      <AddShipmentTrackingLinkForm
        shipmentId={shipment.info.id}
        shipmentType={shipment.info.type}
        onRefresh={refreshShipment}
        buttonLabel={addButtonLabel}
        alertComponent={addPromptComponent}
      />
    </>
  );
}
